import { Suspense, lazy, useEffect } from 'react';
import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom';
import { AppShell } from '@/components/layout/AppShell';
import { QuarryOnly, RequireAuth, RequireConcrete, RequireRole, ModuleGate } from '@/components/RequireAuth';
import { UpdatePrompt } from '@/components/UpdatePrompt';
import { Login } from '@/pages/Login';
import { SetPassword } from '@/pages/SetPassword';
import { Dashboard } from '@/pages/Dashboard';
import { DiaryLists } from '@/pages/diary/DiaryLists';
import { NotFoundPage } from '@/pages/modules';
import { Breakdowns } from '@/pages/Breakdowns';
import { Maintenance } from '@/pages/Maintenance';
import { Parts } from '@/pages/Parts';
// Recharts is ~105 KB gzipped. A supervisor capturing a diary on a tablet at
// the face never opens a chart, so the production screen — the only consumer —
// is split out and fetched on demand.
const Production = lazy(() => import('@/pages/Production').then((m) => ({ default: m.Production })));
// Reports carry a spreadsheet writer behind an on-demand import and are read
// by two people a day, so they are not part of the first load either.
const Reports = lazy(() => import('@/pages/Reports').then((m) => ({ default: m.Reports })));
// The guides are ~150 KB of text and a Markdown renderer; fetched only when
// someone opens Help.
const Help = lazy(() => import('@/pages/Help').then((m) => ({ default: m.Help })));

// The concrete module is its own chunk: the quarry never loads it, and a
// branch tablet loads little else.
const ConcreteDashboard = lazy(() => import('@/pages/concrete/ConcreteDashboard').then((m) => ({ default: m.ConcreteDashboard })));
const Deliveries = lazy(() => import('@/pages/concrete/Deliveries').then((m) => ({ default: m.Deliveries })));
const ConcreteTargets = lazy(() => import('@/pages/concrete/ConcreteTargets').then((m) => ({ default: m.ConcreteTargets })));
const VehicleChecks = lazy(() => import('@/pages/fleet/VehicleChecks').then((m) => ({ default: m.VehicleChecks })));
const BranchFleet = lazy(() => import('@/pages/fleet/BranchFleet').then((m) => ({ default: m.BranchFleet })));
const BranchBreakdowns = lazy(() => import('@/pages/fleet/BranchBreakdowns').then((m) => ({ default: m.BranchBreakdowns })));
const Company = lazy(() => import('@/pages/company/Company').then((m) => ({ default: m.Company })));
const Workshop = lazy(() => import('@/pages/workshop/Workshop').then((m) => ({ default: m.Workshop })));
const ConcreteSettings = lazy(() => import('@/pages/concrete/ConcreteSettings').then((m) => ({ default: m.ConcreteSettings })));
const ConcreteLists = lazy(() => import('@/pages/concrete/ConcreteLists').then((m) => ({ default: m.ConcreteLists })));
// Brick Plant and Pre-cast (0063): one chunk for both.
const PlantModule = lazy(() => import('@/pages/plant/PlantModule').then((m) => ({ default: m.PlantModule })));
// Fuel (0042): every site, quarry and branches alike.
const Fuel = lazy(() => import('@/pages/fuel/Fuel').then((m) => ({ default: m.Fuel })));

import { Blasts } from '@/pages/Blasts';
import { Targets } from '@/pages/Targets';
import { Diary } from '@/pages/Diary';
import { DiaryEditor } from '@/pages/diary/DiaryEditor';
import { Machines } from '@/pages/Machines';
import { Admin } from '@/pages/Admin';
import { Toaster } from '@/components/ui/Toaster';
import { ConfigError } from '@/pages/ConfigError';
import { isConfigured } from '@/lib/supabase';
import { useAuthStore } from '@/stores/authStore';

export function App() {
  const initialise = useAuthStore((s) => s.initialise);
  useEffect(() => {
    if (isConfigured) void initialise();
  }, [initialise]);

  if (!isConfigured) return <ConfigError />;

  return (
    <BrowserRouter>
      <UpdatePrompt />
      <Toaster />
      <Routes>
        <Route path="/login" element={<Login />} />
        {/* Where invitation and password-reset links land. Public by
            necessity: the person following one has no session yet. */}
        <Route path="/set-password" element={<SetPassword />} />
        <Route
          path="/"
          element={
            <RequireAuth>
              <AppShell />
            </RequireAuth>
          }
        >
          <Route element={<ModuleGate />}>
          <Route path="diary" element={<Diary />} />
          <Route path="diary/new" element={<DiaryEditor />} />
          <Route path="diary/lists/:entity?" element={<DiaryLists />} />
          <Route path="diary/:id" element={<DiaryEditor />} />
          <Route element={<QuarryOnly />}>
          <Route index element={<Dashboard />} />
          <Route path="machines" element={<Machines />} />
          <Route
            path="production"
            element={
              <RequireRole roles={['supervisor', 'manager', 'admin']}>
                <Suspense fallback={<div className="centered"><span className="spinner" /></div>}>
                  <Production />
                </Suspense>
              </RequireRole>
            }
          />
          <Route
            path="blasts"
            element={
              <RequireRole roles={['supervisor', 'manager', 'admin']}>
                <Blasts />
              </RequireRole>
            }
          />
          <Route path="breakdowns" element={<Breakdowns />} />
          <Route
            path="maintenance"
            element={
              <RequireRole roles={['maintenance', 'manager', 'admin']}>
                <Maintenance />
              </RequireRole>
            }
          />
          <Route
            path="company"
            element={
              <RequireRole roles={['manager', 'admin']}>
                <Suspense fallback={<div className="centered"><span className="spinner" /></div>}>
                  <Company />
                </Suspense>
              </RequireRole>
            }
          />
          <Route
            path="workshop/*"
            element={
              <RequireRole roles={['maintenance', 'manager', 'admin']}>
                <Suspense fallback={<div className="centered"><span className="spinner" /></div>}>
                  <Workshop />
                </Suspense>
              </RequireRole>
            }
          />
          <Route
            path="parts"
            element={
              <RequireRole roles={['maintenance', 'manager', 'admin']}>
                <Parts />
              </RequireRole>
            }
          />
          <Route
            path="targets"
            element={
              <RequireRole roles={['manager', 'admin']}>
                <Targets />
              </RequireRole>
            }
          />
          <Route
            path="reports"
            element={
              <RequireRole roles={['supervisor', 'manager', 'admin']}>
                <Suspense fallback={<div className="centered"><span className="spinner" /></div>}>
                  <Reports />
                </Suspense>
              </RequireRole>
            }
          />
          <Route
            path="admin/*"
            element={
              <RequireRole roles={['manager', 'admin']}>
                <Admin />
              </RequireRole>
            }
          />
          </Route>
          <Route
            path="concrete"
            element={<RequireConcrete><Suspense fallback={<div className="centered"><span className="spinner" /></div>}><ConcreteDashboard /></Suspense></RequireConcrete>}
          />
          <Route
            path="concrete/deliveries"
            element={<RequireConcrete><Suspense fallback={<div className="centered"><span className="spinner" /></div>}><Deliveries /></Suspense></RequireConcrete>}
          />
          <Route
            path="concrete/targets"
            element={<RequireConcrete><Suspense fallback={<div className="centered"><span className="spinner" /></div>}><ConcreteTargets /></Suspense></RequireConcrete>}
          />
          <Route
            path="concrete/settings"
            element={<RequireConcrete><Suspense fallback={<div className="centered"><span className="spinner" /></div>}><ConcreteSettings /></Suspense></RequireConcrete>}
          />
          <Route
            path="concrete/lists/:entity?"
            element={<RequireConcrete><Suspense fallback={<div className="centered"><span className="spinner" /></div>}><ConcreteLists /></Suspense></RequireConcrete>}
          />
          <Route
            path="brick/:tab?"
            element={<Suspense fallback={<div className="centered"><span className="spinner" /></div>}><PlantModule key="brick" op="brick" /></Suspense>}
          />
          <Route
            path="precast/:tab?"
            element={<Suspense fallback={<div className="centered"><span className="spinner" /></div>}><PlantModule key="precast" op="precast" /></Suspense>}
          />
          <Route
            path="fleet/checks"
            element={<Suspense fallback={<div className="centered"><span className="spinner" /></div>}><VehicleChecks /></Suspense>}
          />
          <Route
            path="fleet/branch"
            element={<Suspense fallback={<div className="centered"><span className="spinner" /></div>}><BranchFleet /></Suspense>}
          />
          <Route
            path="fleet/breakdowns"
            element={<Suspense fallback={<div className="centered"><span className="spinner" /></div>}><BranchBreakdowns /></Suspense>}
          />
          <Route
            path="fuel/*"
            element={<Suspense fallback={<div className="centered"><span className="spinner" /></div>}><Fuel /></Suspense>}
          />
          <Route
            path="help"
            element={
              <Suspense fallback={<div className="centered"><span className="spinner" /></div>}>
                <Help />
              </Suspense>
            }
          />
          </Route>
          <Route path="404" element={<NotFoundPage />} />
          <Route path="*" element={<Navigate to="/404" replace />} />
        </Route>
      </Routes>
    </BrowserRouter>
  );
}
